import { useEffect, useRef } from "react";
import CallRingtone from "src/sounds/rington.wav";

const INBOUND_VOLUME = 0.85;
const OUTBOUND_VOLUME = 0.55;

const isRingingStatus = (status: string | undefined): boolean =>
    status === "inbound" || status === "outbound";

const getRingtoneVolume = (status: string | undefined): number =>
    status === "inbound" ? INBOUND_VOLUME : OUTBOUND_VOLUME;

const stopRingtone = (ringtone: HTMLAudioElement): void => {
    ringtone.pause();
    ringtone.currentTime = 0;
};

export const useCallRingtone = (status: string | undefined, suppressRingtone: boolean): void => {
    const ringtoneRef = useRef<HTMLAudioElement | null>(null);
    const statusRef = useRef(status);
    const shouldRingRef = useRef(false);

    statusRef.current = status;
    shouldRingRef.current = !suppressRingtone && isRingingStatus(status);

    useEffect(() => {
        const ringtone = new Audio(CallRingtone);
        ringtone.loop = true;
        ringtone.preload = "auto";
        ringtoneRef.current = ringtone;

        const removeUnlockListeners = (): void => {
            document.removeEventListener("pointerdown", unlockAudio, true);
            document.removeEventListener("keydown", unlockAudio, true);
            document.removeEventListener("touchstart", unlockAudio, true);
        };

        const unlockAudio = (): void => {
            const shouldRing = shouldRingRef.current;
            ringtone.muted = !shouldRing;
            ringtone.volume = shouldRing ? getRingtoneVolume(statusRef.current) : 0;

            void ringtone.play().then(() => {
                removeUnlockListeners();
                if (shouldRingRef.current) {
                    ringtone.muted = false;
                    ringtone.volume = getRingtoneVolume(statusRef.current);
                    return;
                }

                stopRingtone(ringtone);
                ringtone.muted = false;
                ringtone.volume = INBOUND_VOLUME;
            }).catch(() => {
                ringtone.muted = false;
            });
        };

        document.addEventListener("pointerdown", unlockAudio, { capture: true, passive: true });
        document.addEventListener("keydown", unlockAudio, { capture: true });
        document.addEventListener("touchstart", unlockAudio, { capture: true, passive: true });

        return () => {
            removeUnlockListeners();
            stopRingtone(ringtone);
            ringtone.src = "";
            ringtoneRef.current = null;
        };
    }, []);

    useEffect(() => {
        const ringtone = ringtoneRef.current;
        if (!ringtone) {
            return undefined;
        }

        stopRingtone(ringtone);
        if (!shouldRingRef.current) {
            return undefined;
        }

        ringtone.muted = false;
        ringtone.volume = getRingtoneVolume(status);
        void ringtone.play().catch(error => {
            console.warn("Unable to autoplay call ringtone; waiting for user interaction.", error);
        });

        return () => stopRingtone(ringtone);
    }, [status, suppressRingtone]);
};
